import { infiniteQueryOptions, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'preact/hooks';
import Pubnub from 'pubnub';
import { usePubNub } from 'pubnub-react';

import { FeedMessageWithMeta } from './types';
import { FeedPage, fetchVisiblePage, isWithdrawn, withAction } from './withdrawal';

const CHANNEL = 'communityUpdates';

const queryFn = async (
  pubnub: Pubnub | undefined,
  channelId: string,
  params: Omit<Pubnub.FetchMessagesParameters, 'channels'>,
) => {
  return new Promise<FeedPage>((resolve, reject) => {
    pubnub?.fetchMessages(
      {
        channels: [channelId],
        ...params,
        start: params.start ?? `${Date.now()}0000`,
      },
      (status, resp) => {
        if (status.error) {
          reject(status.errorData);
          return;
        }
        resolve({
          more: resp?.more,
          messages: (resp?.channels?.[CHANNEL] ?? []) as FeedMessageWithMeta[],
        });
      },
    );
  });
};

export const getCommunityFeedInfiniteQueryOptions = (pubnub: Pubnub | undefined) =>
  infiniteQueryOptions({
    queryKey: [pubnub, 'messages', 'list', CHANNEL],
    // `count: 100` although only 25 come back with actions: asked for 25, PubNub sends no `more`.
    queryFn: ({ pageParam }) =>
      fetchVisiblePage((start) => queryFn(pubnub, CHANNEL, { start, count: 100, includeMessageActions: true }), pageParam),
    enabled: !!pubnub,
    initialPageParam: '',
    getNextPageParam: (lastPage: FeedPage) => lastPage?.more?.start?.toString(),
    // Filtered again here for updates withdrawn after their page was fetched.
    select: ({ pages }) => pages.flatMap((page) => [...page.messages].reverse()).filter((message) => !isWithdrawn(message)),
    // data will be updated through pubnub listener
    staleTime: Infinity,
    refetchOnMount: 'always',
  });

export const useCommunityFeedInfiniteQuery = () => {
  const pubnub = usePubNub();
  const queryClient = useQueryClient();
  const query = useInfiniteQuery(getCommunityFeedInfiniteQueryOptions(pubnub));

  useEffect(() => {
    const { queryKey } = getCommunityFeedInfiniteQueryOptions(pubnub);
    const handler: Pubnub.ListenerParameters['message'] = (event) => {
      if (event.channel !== CHANNEL) return;
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) return oldData;
        const firstPage = oldData.pages?.[0];
        if (!firstPage) return oldData;
        if (firstPage.messages.some((message) => message.timetoken === event.timetoken)) return oldData;
        // A live message names its sender `publisher`; history calls it `uuid`.
        const message = { ...event, uuid: event.publisher } as FeedMessageWithMeta;
        const newData = { more: firstPage.more, messages: firstPage.messages.concat(message) };
        return {
          ...oldData,
          pages: [newData, ...oldData.pages.slice(1)],
        };
      });
    };
    const actionHandler: Pubnub.ListenerParameters['messageAction'] = ({ channel, event, data }) => {
      if (channel !== CHANNEL || event !== 'added' || data.type !== 'deleted') return;
      if (!queryClient.getQueryData(queryKey)) {
        // The first page is still loading and may have been read before this action landed.
        // Invalidating cancels that load and starts another, whose read includes the action.
        queryClient.invalidateQueries({ queryKey });
        return;
      }
      queryClient.setQueryData(
        queryKey,
        (oldData) =>
          oldData && {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              messages: page.messages.map((message) =>
                String(message.timetoken) === data.messageTimetoken ? withAction(message, data) : message,
              ),
            })),
          },
      );
    };
    pubnub.addListener({ message: handler, messageAction: actionHandler });
    pubnub.subscribe({ channels: [CHANNEL] });
    return () => {
      pubnub.unsubscribe({ channels: [CHANNEL] });
      pubnub.removeListener({ message: handler, messageAction: actionHandler });
    };
  }, [pubnub, queryClient]);

  return query;
};
